import { createContext, useCallback, useEffect, useReducer, useRef } from 'react';
import firebase from 'src/utils/firebase';
import { useDispatch as useStoreDispatch } from 'react-redux';
import { setCalendarScope } from 'src/slices/calendar';
import PropTypes from 'prop-types';
import axios from 'axios';
import { buildApiUrl } from 'src/utils/api';
import { applyActiveShop, getActiveShopId } from 'src/utils/shopContext';
import { markStartup, measureStartup } from 'src/utils/startupPerformance';

const initialAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  needsProvisioning: false,
  accountError: null,
  sessionRevision: 0
};

const reducer = (state, action) => {
  if (action.type === 'AUTH_CONTEXT_PENDING') {
    return { ...state, isAuthenticated: false, isInitialized: false, user: null, needsProvisioning: false, accountError: null };
  }

  if (action.type === 'AUTH_STATE_CHANGED') {
    const { isAuthenticated, user, needsProvisioning = false, accountError = null } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      needsProvisioning,
      accountError
    };
  }

  if (action.type === 'TOKEN_REFRESHED') {
    return {
      ...state,
      sessionRevision: state.sessionRevision + 1
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'FirebaseAuth',
  createUserWithEmailAndPassword: () => Promise.resolve(),
  signInWithEmailAndPassword: () => Promise.resolve(),
  signInWithGoogle: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  registerTenant: () => Promise.resolve(),
  refreshAccountContext: () => Promise.resolve(),
  setActiveShop: () => Promise.resolve()
});

const contextChanged = () => new Error('Your account changed. Please try again.');

const loadAccountContext = async (firebaseUser, requestedShopId = getActiveShopId(), isCurrent = () => true) => {
  const idToken = await firebaseUser.getIdToken();
  const request = (shopId) => {
    if (!isCurrent()) throw contextChanged();
    return axios.get(buildApiUrl('/api/auth/context'), {
      headers: {
        Authorization: `Bearer ${idToken}`,
        // Null explicitly suppresses an old Axios default during the fallback.
        'X-Shop-Id': shopId ? String(shopId) : null
      }
    });
  };

  try {
    return (await request(requestedShopId)).data;
  } catch (error) {
    if (requestedShopId && Number(error?.response?.status) === 403) {
      // Persist the selected shop only once a current account response is accepted.
      return (await request(null)).data;
    }
    throw error;
  }
};

const buildUser = (firebaseUser, account) => ({
  id: firebaseUser.uid,
  uid: firebaseUser.uid,
  avatar: firebaseUser.photoURL,
  email: firebaseUser.email,
  emailVerified: Boolean(firebaseUser.emailVerified),
  accountVersion: Number(account?.user?.accountVersion || 1),
  name: account?.user?.displayName || firebaseUser.displayName || firebaseUser.email,
  role: account?.role || 'advisor',
  employeeId: account?.employeeId || account?.user?.employeeId || null,
  shop: account?.shop || null,
  shops: Array.isArray(account?.shops) ? account.shops : account?.shop ? [account.shop] : []
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const storeDispatch = useStoreDispatch();
  const mounted = useRef(true);
  const identity = useRef(null);
  const requestGeneration = useRef(0);
  const sessionGeneration = useRef(0);
  const renderedSessionGeneration = sessionGeneration.current;

  const clearCalendar = useCallback(() => storeDispatch(setCalendarScope({ identityId: null, shopId: null })), [storeDispatch]);
  const beginRequest = useCallback((user) => {
    const generation = ++requestGeneration.current;
    return () => mounted.current && generation === requestGeneration.current &&
      firebase.auth().currentUser?.uid === user?.uid;
  }, []);
  const publishAccount = useCallback((nextUser, extra = {}) => {
    // Redux invalidation happens synchronously before React can render the next identity/shop.
    storeDispatch(setCalendarScope({ identityId: nextUser?.uid, shopId: nextUser?.shop?.id }));
    applyActiveShop(nextUser?.shop?.id || null);
    dispatch({
      type: 'AUTH_STATE_CHANGED',
      payload: { isAuthenticated: Boolean(nextUser), user: nextUser, ...extra }
    });
  }, [storeDispatch]);

  useEffect(() => {
    mounted.current = true;
    markStartup('auth-listener-start');
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
        if ((user?.uid || null) !== (firebase.auth().currentUser?.uid || null)) return;
        if (identity.current !== (user?.uid || null)) {
          sessionGeneration.current += 1;
          clearCalendar();
          identity.current = user?.uid || null;
          // Do not leave the previous account visible during context hydration.
          dispatch({ type: 'AUTH_CONTEXT_PENDING' });
        }
        const isCurrent = beginRequest(user);
        if (user) {
          let account = null;
          let needsProvisioning = false;
          let accountError = null;
          try {
            account = await loadAccountContext(user, getActiveShopId(), isCurrent);
            markStartup('account-context-ready');
            measureStartup('account-context', 'auth-listener-start', 'account-context-ready');
          } catch (error) {
            const status = Number(error?.response?.status);
            const code = String(error?.response?.data?.code || '');
            needsProvisioning = status === 409 && code === 'SHOP_PROVISIONING_REQUIRED';
            if (!needsProvisioning) {
              accountError = error?.response?.data?.error || error?.message || 'Account context is unavailable.';
              if (isCurrent()) console.warn('[auth] account context unavailable');
            }
          }
          if (!isCurrent()) return;
          const nextUser = buildUser(user, account);
          publishAccount(nextUser, { needsProvisioning, accountError });
          markStartup('authentication-ready');
        } else {
          clearCalendar();
          publishAccount(null);
        }
      });
    return () => {
      mounted.current = false;
      requestGeneration.current += 1;
      unsubscribe();
    };
  }, [beginRequest, clearCalendar, publishAccount]);

  useEffect(() => {
    const unsubscribe = firebase.auth().onIdTokenChanged((user) => {
      if (user && user.uid === identity.current && user.uid === firebase.auth().currentUser?.uid) {
        dispatch({ type: 'TOKEN_REFRESHED' });
      }
    });
    return unsubscribe;
  }, []);

  const signInWithEmailAndPassword = (email, password) => {
    return firebase.auth().signInWithEmailAndPassword(email, password);
  };

  const signInWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();

    return firebase.auth().signInWithPopup(provider);
  };

  const createUserWithEmailAndPassword = async (email, password) => {
    return firebase.auth().createUserWithEmailAndPassword(email, password);
  };

  const registerTenant = async (details) => {
    const registrationSession = sessionGeneration.current;
    const response = await axios.post(buildApiUrl('/api/auth/register'), details);
    if (!mounted.current || registrationSession !== sessionGeneration.current) throw contextChanged();
    const credential = await firebase.auth().signInWithCustomToken(response.data.customToken);
    if (firebase.auth().currentUser?.uid !== credential.user.uid) throw contextChanged();
    const isCurrent = beginRequest(credential.user);
    try {
      await credential.user.sendEmailVerification({
        url: `${window.location.origin}/account/login`
      });
    } catch (error) {
      // The account is already durable; the verification screen provides a resend action.
      console.warn('[auth] initial verification email could not be sent', error?.message || error);
    }
    if (!isCurrent()) throw contextChanged();
    await credential.user.reload();
    if (!isCurrent()) throw contextChanged();
    const nextUser = buildUser(credential.user, response.data);
    publishAccount(nextUser);
    return response.data;
  };

  const refreshAccountContext = async () => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser || currentUser.uid !== identity.current ||
        renderedSessionGeneration !== sessionGeneration.current) return null;
    const isCurrent = beginRequest(currentUser);
    await currentUser.reload();
    if (!isCurrent()) return null;
    await currentUser.getIdToken(true);
    const account = await loadAccountContext(currentUser, getActiveShopId(), isCurrent);
    if (!isCurrent()) return null;
    const nextUser = buildUser(currentUser, account);
    publishAccount(nextUser);
    return nextUser;
  };

  const setActiveShop = async (shopId) => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser || currentUser.uid !== state.user?.uid || currentUser.uid !== identity.current ||
        renderedSessionGeneration !== sessionGeneration.current) throw new Error('Authentication is required.');
    const normalized = Number(shopId || 0);
    if (!state.user?.shops?.some((shop) => Number(shop.id) === normalized)) {
      throw new Error('You do not have access to that shop.');
    }
    if (normalized === Number(state.user?.shop?.id)) return state.user;
    const previousUser = state.user;
    const isCurrent = beginRequest(currentUser);
    clearCalendar();
    try {
      const account = await loadAccountContext(currentUser, normalized, isCurrent);
      if (!isCurrent()) throw contextChanged();
      const nextUser = buildUser(currentUser, account);
      publishAccount(nextUser);
      return nextUser;
    } catch (error) {
      // Failed current switches can reload the original shop; stale completions cannot restore it.
      if (isCurrent()) publishAccount(previousUser);
      throw error;
    }
  };

  const logout = () => {
    sessionGeneration.current += 1;
    requestGeneration.current += 1;
    identity.current = null;
    clearCalendar();
    publishAccount(null);
    return firebase.auth().signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'FirebaseAuth',
        createUserWithEmailAndPassword,
        registerTenant,
        refreshAccountContext,
        setActiveShop,
        signInWithEmailAndPassword,
        signInWithGoogle,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
